<template>
  <div class="deleted-files-manager">
    <v-slide-y-transition>
      <v-alert v-if="allertDelete" dense text color="error" border="left" class="alert-message" icon="mdi-alert-circle">
        <span class="font-weight-medium">Deleted recordings will be permanently deleted after 30 days</span>
      </v-alert>
    </v-slide-y-transition>

    <div class="header-section">
      <v-card class="search-card">
        <v-card-text class="search-container">
          <div class="search-and-count">
            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search" single-line hide-details
              filled rounded dense class="search-field" background-color="rgba(0, 0, 0, 0.03)"></v-text-field>
            <div class="records-count">
              <v-chip color="#f44336" text-color="white" small>
                {{ records.length }}
              </v-chip>
              <span class="count-label">Deleted Recordings</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <div class="table-section">
      <v-card class="table-card">
        <v-data-table :headers="headers" :items="records" sort-by="position" sort-desc :search="search" :footer-props="{
          'items-per-page-options': [20, 50, 100, 500, 1000],
          'items-per-page-text': 'Rows per page:'
        }" :loading="progressShow" loading-text="Loading data..." no-data-text="No deleted recordings"
          class="recordings-table">
          <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
            v-slot:[`header.${header.value}`]="{ header }">
            <div class="custom-header">{{ header.text }}</div>
          </template>

          <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index }">
            <tr class="table-row">
              <td class="table-cell">{{ item.integer_id }}</td>
              <td class="table-cell">{{ item.caller_name }}</td>
              <td class="table-cell">{{ item.caller_id }}</td>
              <td class="table-cell">
                <div class="type-cell">
                  <span>{{ item.type }}</span>
                  <v-tooltip bottom color="#0d2c6d" v-if="item.type === 'Shared Message'">
                    <template v-slot:activator="{ on }">
                      <v-icon small color="#0d2c6d" v-on="on" class="info-icon">mdi-information</v-icon>
                    </template>
                    <span>Shared from system number: {{ item.shared_from_ivr }}</span>
                  </v-tooltip>
                </div>
              </td>
              <td class="table-cell date-cell">{{ item.createdAt }}</td>
              <td class="table-cell date-cell">{{ item.deletedAt }}</td>
              <td class="table-cell">{{ item.record_duration }}</td>
              <td class="table-cell">{{ item.record_name_original }}</td>
              <td class="table-cell">
                <div class="audio-controls">
                  <v-tooltip bottom v-if="item.type !== 'Private Message'">
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="stop(item)" class="control-btn">
                        <v-icon small>mdi-stop</v-icon>
                      </v-btn>
                    </template>
                    <span>Stop</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="item.type !== 'Private Message'">
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="pause(item)" class="control-btn">
                        <v-icon small>mdi-pause</v-icon>
                      </v-btn>
                    </template>
                    <span>Pause</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="item.type !== 'Private Message'">
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="play(item)" class="control-btn play-btn">
                        <v-icon small>mdi-play</v-icon>
                      </v-btn>
                    </template>
                    <span>Play</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="item.type !== 'Private Message'">
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="download(item)" class="control-btn download-btn">
                        <v-icon small>mdi-download</v-icon>
                      </v-btn>
                    </template>
                    <span>Download</span>
                  </v-tooltip>
                </div>
              </td>
              <td class="table-cell">
                <div class="action-buttons">
                  <v-tooltip bottom v-if="item.type !== 'Private Message'">
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="openStatisticDialog(item, index)"
                        class="action-btn stats-btn">
                        <v-icon small>mdi-chart-bar</v-icon>
                      </v-btn>
                    </template>
                    <span>Listening Statistics</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="openDeleteDialog(item, index)"
                        class="action-btn delete-btn">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete Permanently</span>
                  </v-tooltip>
                </div>

              </td>
            </tr>
          </template>

          <template v-slot:item.actions="{ item, index }">
            <div class="mobile-actions">
              <v-tooltip bottom v-if="item.type !== 'Private Message'">
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="openStatisticDialog(item, index)" class="action-btn stats-btn">
                    <v-icon small>mdi-chart-bar</v-icon>
                  </v-btn>
                </template>
                <span>Listening Statistics</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.type="{ item }">
            <div class="type-cell">
              <span>{{ item.type }}</span>
              <v-tooltip bottom color="#0d2c6d" v-if="item.type === 'Shared Message'">
                <template v-slot:activator="{ on }">
                  <v-icon small color="#0d2c6d" v-on="on" class="info-icon">mdi-information</v-icon>
                </template>
                <span>Shared from system number: {{ item.shared_from_ivr }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.record_name="{ item }">
            <div class="mobile-controls">
              <v-tooltip bottom v-if="item.type !== 'Private Message'">
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="stop(item)" class="control-btn">
                    <v-icon small>mdi-stop</v-icon>
                  </v-btn>
                </template>
                <span>Stop</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.type !== 'Private Message'">
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="pause(item)" class="control-btn">
                    <v-icon small>mdi-pause</v-icon>
                  </v-btn>
                </template>
                <span>Pause</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.type !== 'Private Message'">
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="play(item)" class="control-btn play-btn">
                    <v-icon small>mdi-play</v-icon>
                  </v-btn>
                </template>
                <span>Play</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.type !== 'Private Message'">
                <template v-slot:activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="download(item)" class="control-btn download-btn">
                    <v-icon small>mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="no-data-container">
              <v-icon large color="#f44336" class="mb-4">mdi-delete-empty</v-icon>
              <div class="no-data-text">No deleted recordings</div>
              <v-btn color="#0d2c6d" outlined @click="refreshDetails()" class="refresh-btn mt-4">
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <v-overlay :value="overlay" class="loading-overlay">
      <v-progress-circular indeterminate size="64" width="4" color="#0d2c6d"></v-progress-circular>
    </v-overlay>


    <ListeningStatisticDialog v-model="listeningStatisticDialog" v-if="listeningStatisticDialog"
      :listeningDetails="listeningDetails" :recordDuration="recordDuration"></ListeningStatisticDialog>

    <DeleteItemDialog v-model="deleteDialog" v-if="deleteDialog" :item="itemToDelete" actionType="hardDelete"
      @itemDeleted="itemDeleted">
    </DeleteItemDialog>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import utils from '@/util/utils'
import Auth from '@/services/auth.service.js'
import ListeningStatisticDialog from '@/components/upload/dialogs/ListeningStatisticDialog'
import DeleteItemDialog from '@/components/upload/dialogs/DeleteItemDialog'

export default {
  components: {
    SnackBar,
    DeleteItemDialog,
    ListeningStatisticDialog
  },
  data: () => ({
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    progressShow: false,
    lineNumber: "",
    records: [],
    itemToDelete: {},
    deleteDialog: false,
    listeningStatisticDialog: false,
    listeningDetails: [],
    recordDuration: "",
    overlay: false,
    search: "",
    allertDelete: true,
    headers: [
      { text: "File No.", value: "integer_id", align: 'center' },
      { text: 'Name', align: 'center', value: 'caller_name' },
      { text: 'Phone', value: 'caller_id', align: 'center' },
      { text: 'Type', value: 'type', align: 'center' },
      { text: 'Recording Date', value: 'createdAt', align: 'center' },
      { text: 'Deleted Date', value: 'deletedAt', align: 'center' },
      { text: 'Duration', value: 'record_duration', align: 'center' },
      { text: 'Recording Name', value: 'record_name_original', align: 'center' },
      { text: 'Audio', value: 'record_name', align: 'center' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
    ],
  }),
  methods: {
    async getRecords() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/records/get_deleted_records`;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.json();

        if (res.status >= 400) {
          this.showSnackBar("Error fetching data", "red");
        } else if (res.status === 200) {
          this.records = jsonObject;
          this.formatDetails();
        }
        this.progressShow = false;
      } catch (error) {
        this.progressShow = false;
        this.showSnackBar("Error get files: " + error, "red");
      }
    },

    formatDetails() {
      this.records = this.records.map((record, index) => {
        return {
          ...record,
          createdAt: utils.getCurrentDate(record.createdAt),
          deletedAt: utils.getCurrentDate(record.deletedAt),
          record_duration_original: record.record_duration,
          record_duration: this.formatDuration(record.record_duration),
          type: this.getRecordTypeText(record.type),
          caller_id: record.caller_id === "WEBSITE" ? "Admin Panel" : record.caller_id,
          position: index + 1 // Adding a position property
        }
      });
    },
    getRecordTypeText(type) {
      const types = {
        regular: 'Extension 1 Message',
        private: 'Private Message',
        managers: 'Manager Message',
        pending: 'Scheduled Message',
        shared: 'Shared Message',
        survey: 'Survey Message',
        await_approval: 'Await Approval Message',
      };

      return types[type] || 'הודעה משותפת';
    },

    async setAudio(record) {
      try {
        let token = localStorage.getItem("token");
        let api = process.env.VUE_APP_BASE_URL + `/` + record.record_name;
        const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
        const jsonObject = await res.blob();

        if (res.status >= 400) {
          this.showSnackBar("Error playing recording", "red");
        } else if (res.status === 200) {
          const url = URL.createObjectURL(jsonObject);
          record.record_link = url;
          record.record_audio = new Audio(url);
          const index = this.records.findIndex(record1 => record1._id === record._id);
          Object.assign(this.records[index], record);
        }
      } catch (error) {
        this.showSnackBar("Error get files: " + error, "red");
      }
    },
    openDeleteDialog(item, index) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    itemDeleted(id, text, color) {
      if (id === "") {
        this.showSnackBar(text, color);
        return;
      }

      // Use splice to remove the item at the specified index
      const index = this.records.findIndex(record => record._id === id);

      this.records.splice(index, 1);
      this.showSnackBar(text, color);
    },
    formatDuration(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const sec = seconds % 60;

      const paddedHours = hours.toString().padStart(2, '0');
      const paddedMinutes = minutes.toString().padStart(2, '0');
      const paddedSeconds = sec.toString().padStart(2, '0');

      return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true;
    },

    async download(item) {
      if (!item.record_audio) {
        this.overlay = true;
        await this.setAudio(item);
        this.overlay = false;
      }

      // Create a link for downloading
      const downloadLink = document.createElement("a");
      downloadLink.href = item.record_link; // URL to the audio file
      downloadLink.download = item.record_name; // The name of the downloaded file

      // Append link to the body, click it, and then remove it
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    },

    async play(item) {
      if (!item.record_audio) {
        this.overlay = true;
        await this.setAudio(item);
        this.overlay = false;
      }
      console.log();


      item.record_audio.play();
      item.record_audio.addEventListener('ended', () => {
        this.stop(item);
      });
      item.record_audio.addEventListener('error', (event) => {
        this.stop(item);
        this.showSnackBar('Error: Invalid audio file', "red");
      });
    },

    stop(item) {
      if (item.record_audio) {
        item.record_audio.pause();
        item.record_audio.currentTime = 0;
      }
    },

    pause(item) {
      if (item.record_audio) {
        item.record_audio.pause();
      }
    },

    openStatisticDialog(item, index) {
      this.listeningDetails = item.listening;
      this.recordDuration = item.record_duration_original;
      this.listeningStatisticDialog = true;
    },

    refreshDetails() {
      this.getRecords();
    }
  },
  mounted() {
    this.lineNumber = Auth.getLineNumber();
    this.getRecords();

    // Hide alert after 5 seconds
    setTimeout(() => {
      this.allertDelete = false;
    }, 5000);
  },
}
</script>

<style scoped>
/* Keeping styles exactly as they were */
.deleted-files-manager {
  padding: 0 16px;
}

.alert-message {
  margin-bottom: 16px;
  border-radius: 8px;
}

.header-section {
  margin-bottom: 24px;
}

.search-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.search-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.search-and-count {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
}

.search-field {
  max-width: 300px;
}

.records-count {
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

.table-section {
  margin-bottom: 32px;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.recordings-table {
  width: 100%;
}

.custom-header {
  font-size: 14px;
  font-weight: 600;
  color: #0d2c6d;
  text-align: center;
}

.table-row {
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: rgba(13, 44, 109, 0.03) !important;
}

.table-cell {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.8);
  text-align: center !important;
  padding: 12px 8px !important;
}

.date-cell {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.type-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.info-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.info-icon:hover {
  opacity: 1;
}

.audio-controls,
.action-buttons,
.mobile-controls,
.mobile-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.control-btn,
.action-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.control-btn:hover,
.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.play-btn {
  background-color: rgba(13, 44, 109, 0.1);
}

.play-btn:hover {
  background-color: rgba(13, 44, 109, 0.2);
}

.download-btn {
  background-color: rgba(76, 175, 80, 0.1);
}

.download-btn:hover {
  background-color: rgba(76, 175, 80, 0.2);
}

.stats-btn {
  background-color: rgba(255, 185, 26, 0.1);
}

.stats-btn:hover {
  background-color: rgba(255, 185, 26, 0.2);
}

.loading-overlay {
  z-index: 1000;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.no-data-text {
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 16px;
}

.refresh-btn {
  text-transform: none;
  letter-spacing: 0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  background-color: rgba(13, 44, 109, 0.05);
  transform: translateY(-2px);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-and-count {
    flex-direction: column;
    align-items: stretch;
  }

  .search-field {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .deleted-files-manager {
    padding: 0 8px;
  }
}

.v-data-table>>>td,
.v-data-table>>>th {
  text-align: center !important;
}

.v-data-table>>>.v-data-table__wrapper table {
  table-layout: fixed;
}
</style>